import clsx from "clsx";
import {
  FC,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import { CopyButton, ExpandablePanel } from "@tsmono/react/components";
import { useCollapsibleIds } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import { VirtualList } from "@tsmono/react/virtual";
import { isRecord } from "@tsmono/util";

import { copyValueText } from "./copyText";
import { resolveStoreKeys } from "./record_processors/store";
import { RecordProcessor } from "./record_processors/types";
import styles from "./RecordTree.module.css";
import { RenderedContent } from "./RenderedContent";

const kRecordTreeKey = "record-tree-key";

/** VirtualList persistence-key prefix for record trees. Exported so the app's
 *  per-sample reset can clear the persisted snapshots by this prefix. */
export const kMetadataGridKeyPrefix = "metadata-grid-";

interface RecordTreeProps {
  id: string;
  record: Record<string, unknown>;
  className?: string | string[];
  scrollRef?: RefObject<HTMLDivElement | null>;
  defaultExpandLevel?: number;
  processStore?: boolean;
  useBorders?: boolean;
  copyButton?: boolean;
}

/**
 * Renders the MetaDataView component.
 */
export const RecordTree: FC<RecordTreeProps> = ({
  id,
  record,
  className,
  scrollRef,
  defaultExpandLevel = 1,
  processStore = false,
  useBorders = true,
  copyButton = false,
}) => {
  // Collapse state — persisted user choices only. Defaults are applied on
  // the fly at render time by `isItemCollapsed`, so we never bootstrap
  // defaults into persisted state.
  const [collapsedIds, setCollapsed, clearIds] = useCollapsibleIds(id);

  // Clear the collapsed ids when the component unmounts
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    return () => {
      clearIds();
    };
  }, [clearIds, id]);

  // Merge persisted choice with the default-collapse rule.
  // Rule: collapse items with children when they are deeper than
  // `defaultExpandLevel`, or when they have more than 5 direct children.
  const isItemCollapsed = useCallback(
    (
      itemId: string,
      hasChildren: boolean,
      depth: number,
      childCount: number | undefined
    ): boolean => {
      const explicit = collapsedIds?.[itemId];
      if (explicit !== undefined) return explicit;
      if (!hasChildren) return false;
      return (
        depth >= defaultExpandLevel ||
        (childCount !== undefined && childCount > 5)
      );
    },
    [collapsedIds, defaultExpandLevel]
  );

  // Tree-ify the record (creates a flat list of items with depth property).
  // Each item carries its effective `isCollapsed` state so render and
  // recursion decisions share a single source of truth.
  const items = useMemo(() => {
    return toTreeItems(
      record,
      isItemCollapsed,
      processStore ? [resolveStoreKeys] : []
    );
  }, [record, isItemCollapsed, processStore]);

  // Keyboard handling for tree
  const keyUpHandler = useCallback(
    (item: MetadataItem, index: number) => {
      return (event: KeyboardEvent) => {
        switch (event.key) {
          case "Enter":
            event.preventDefault();
            event.stopPropagation();
            setCollapsed(item.id, !item.isCollapsed);
            break;
          case "ArrowDown": {
            event.preventDefault();
            event.stopPropagation();
            // focus next
            if (index === items.length - 1) {
              return;
            }
            const treeRoot = document.getElementById(id);
            const nextEl = treeRoot?.querySelector(
              `.${kRecordTreeKey}[data-index="${index + 1}"]`
            );
            if (nextEl instanceof HTMLElement) {
              nextEl.focus();
            }
            break;
          }
          case "ArrowUp": {
            event.preventDefault();
            event.stopPropagation();
            // focus previous
            if (index === 0) {
              return;
            }
            const treeRoot = document.getElementById(id);
            const prevEl = treeRoot?.querySelector(
              `.${kRecordTreeKey}[data-index="${index - 1}"]`
            );
            if (prevEl instanceof HTMLElement) {
              prevEl.focus();
            }
            break;
          }
          case "ArrowRight":
            event.preventDefault();
            event.stopPropagation();
            setCollapsed(item.id, false);
            break;
          case "ArrowLeft":
            event.preventDefault();
            event.stopPropagation();
            setCollapsed(item.id, true);
            break;
        }
      };
    },
    [setCollapsed, items, id]
  );

  const renderRow = (index: number) => {
    const item = items[index];
    if (!item) {
      return null;
    }

    const showValue =
      item.value !== null && (!item.hasChildren || item.isCollapsed);

    return (
      <div
        key={item.id}
        className={clsx(
          styles.keyPairContainer,
          copyButton && styles.keyPairCopy,
          index < items.length - 1 && useBorders
            ? styles.keyPairBordered
            : undefined,
          "text-size-small"
        )}
        style={{
          paddingLeft: `${item.depth * 20}px`,
        }}
      >
        <div
          data-index={index}
          className={clsx(
            kRecordTreeKey,
            styles.key,
            "font-monospace",
            "text-style-secondary"
          )}
          onKeyUp={keyUpHandler(item, index)}
          tabIndex={0}
          role="button"
          onClick={() => {
            setCollapsed(item.id, !item.isCollapsed);
          }}
        >
          <div>
            {item.hasChildren ? (
              <pre className={clsx(styles.pre)}>
                <Icon
                  name={item.isCollapsed ? "treeClosed" : "treeOpen"}
                  className={styles.treeIcon}
                />
              </pre>
            ) : undefined}
          </div>
          <pre className={clsx(styles.pre)}>{item.key}:</pre>
        </div>
        <div>
          {showValue ? (
            <ExpandablePanel
              id={`${id}-collapse-${item.id}`}
              collapse={true}
              lines={15}
              togglePosition="block-left"
            >
              <RenderedContent
                id={`${id}-value-${item.id}`}
                entry={{
                  name: item.key,
                  value: item.value,
                }}
                renderOptions={{ renderString: "pre" }}
              />
            </ExpandablePanel>
          ) : undefined}
        </div>
        {copyButton && showValue ? (
          <div className={styles.copyCell}>
            <CopyButton
              value={copyValueText(item.rawValue)}
              ariaLabel={`Copy ${item.key}`}
            />
          </div>
        ) : undefined}
      </div>
    );
  };

  if (!scrollRef) {
    // No virtualization - render directly
    return (
      // No tabIndex: neither branch is the scroll container (the host's
      // scroller is), and every row is already its own tab stop.
      <div
        id={id}
        className={clsx(className, "samples-list")}
        style={{ width: "100%" }}
      >
        {items.map((_, index) => renderRow(index))}
      </div>
    );
  }
  return (
    <VirtualList<MetadataItem>
      persistenceKey={`${kMetadataGridKeyPrefix}${id}`}
      id={id}
      scrollRef={scrollRef}
      data={items}
      renderRow={renderRow}
      estimatedItemHeight={50}
      overscan={10}
      embedded={true}
      findScope="none"
      className={clsx(className, "samples-list")}
    />
  );
};

interface MetadataItem {
  id: string;
  key: string;
  value: string | number | boolean | null;
  /** Original (untruncated) value backing this row — objects and arrays
   *  keep their full subtree here while `value` holds the display label. */
  rawValue: unknown;
  depth: number;
  hasChildren: boolean;
  childCount?: number;
  isCollapsed: boolean;
}

type IsCollapsedFn = (
  itemId: string,
  hasChildren: boolean,
  depth: number,
  childCount: number | undefined
) => boolean;

export const toTreeItems = (
  record: Record<string, unknown>,
  isCollapsed: IsCollapsedFn,
  recordProcessors: RecordProcessor[] = [],
  currentDepth = 0,
  currentPath: string[] = []
): MetadataItem[] => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!record) {
    return [];
  }

  // Apply any record processors
  if (recordProcessors.length > 0) {
    for (const processor of recordProcessors) {
      record = processor(record);
    }
  }

  const result: MetadataItem[] = [];

  Object.entries(record).forEach(([key, value], index) => {
    const itemSegment = index.toString();
    result.push(
      ...processNodeRecursive(
        key,
        value,
        currentDepth,
        currentPath,
        itemSegment,
        isCollapsed
      )
    );
  });

  return result;
};

const processNodeRecursive = (
  key: string,
  value: unknown,
  depth: number,
  parentPath: string[],
  thisPath: string,
  isCollapsed: IsCollapsedFn
): MetadataItem[] => {
  const items: MetadataItem[] = [];
  const currentItemPath = [...parentPath, thisPath];
  const id = `${depth}.${currentItemPath.join(".")}`;

  if (isPrimitiveOrNull(value)) {
    items.push({
      id,
      key,
      value: value === undefined ? null : value,
      rawValue: value,
      depth,
      hasChildren: false,
      isCollapsed: false,
    });
    return items;
  }

  // For non-primitives (objects, arrays, functions, etc.)
  let displayValue: string | number | boolean | null;
  let processChildren: boolean;
  let childCount: number | undefined;

  if (Array.isArray(value)) {
    processChildren = true;
    childCount = value.length;
    displayValue = `Array(${value.length})`;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (typeof value === "object" && value !== null) {
    processChildren = true;
    childCount = Object.keys(value).length;
    displayValue = `Object(${Object.keys(value).length})`;
  } else if (
    typeof value === "function" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  ) {
    // Other types with a meaningful toString. Treated as leaf nodes.
    displayValue = value.toString();
    processChildren = false;
  } else {
    displayValue = null;
    processChildren = false;
  }

  const collapsed = processChildren
    ? isCollapsed(id, true, depth, childCount)
    : false;

  // Add the item
  items.push({
    id,
    key,
    value: displayValue,
    rawValue: value,
    depth,
    hasChildren: processChildren,
    childCount,
    isCollapsed: collapsed,
  });

  // Process children
  if (processChildren && !collapsed) {
    const childDepth = depth + 1;
    if (Array.isArray(value)) {
      if (value.length > 0) {
        value.forEach((element, index) => {
          const elementKey = `[${index}]`;
          const elementIdentifier = `[${index}]`;
          items.push(
            ...processNodeRecursive(
              elementKey,
              element,
              childDepth,
              currentItemPath,
              elementIdentifier,
              isCollapsed
            )
          );
        });
      }
    } else if (isRecord(value)) {
      // Process object properties
      Object.entries(value).forEach(([childKey, childValue], index) => {
        const childIdentifier = index.toString();
        items.push(
          ...processNodeRecursive(
            childKey,
            childValue,
            childDepth,
            currentItemPath,
            childIdentifier,
            isCollapsed
          )
        );
      });
    }
  }

  return items;
};

const isPrimitiveOrNull = (
  value: unknown
): value is string | number | boolean | null | undefined => {
  return (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
};
